import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChartPie, Layers3, Loader2, MoreVertical, Utensils } from 'lucide-react';

import { useActiveSubject } from '../context/ActiveSubjectContext';
import {
  loadHomeCollectionStats,
  peekHomeCollectionStats,
  resolveActiveSubjectCoveragePercent,
  resolveActiveSubjectParticleCount,
  resolveActiveSubjectReviewCount,
  type HomeCollectionStats,
} from '../services/homeCollectionStatsService';
import {
  getHomeTtqBurdenGeneration,
  loadHomeTtqBurdenStats,
  peekHomeTtqBurdenStats,
  subscribeHomeTtqBurdenCache,
  type HomeTtqBurdenStats,
} from '../services/homeTtqBurdenService';
import { isTtqSubjectEnabled } from '../services/ttqService';
import { SUBJECT_CONFIG, type SubjectCode } from '../utils/subjectConfig';

type HomeCollectionStatsBannerProps = {
  userId: string;
  /** 变化时重新拉取（回首页、学科切换、页签可见） */
  refreshKey?: string | number;
};

function formatStatValue(value: number) {
  if (value > 9999) return '9999+';
  return String(Math.max(0, value));
}

function formatCoverageValue(value: number) {
  return `${Math.max(0, Math.min(100, Math.round(value)))}%`;
}

function formatBurdenValue(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 10) / 10;
  return `${rounded % 1 === 0 ? String(rounded) : rounded.toFixed(1)}%`;
}

/** 还需复习颗粒数 ≈ 总颗粒数 × (1 − 负担减少比例) */
function computeNeedReviewParticleCount(
  subjectParticleTotal: number | null | undefined,
  burdenReductionPercent: number | null | undefined,
): number | null {
  if (subjectParticleTotal == null || !Number.isFinite(subjectParticleTotal) || subjectParticleTotal < 0) {
    return null;
  }
  if (burdenReductionPercent == null || !Number.isFinite(burdenReductionPercent)) {
    return null;
  }
  const ratio = Math.max(0, Math.min(1, 1 - burdenReductionPercent / 100));
  return Math.max(0, Math.round(subjectParticleTotal * ratio));
}

function SideStatPanel({
  loading,
  value,
  label,
  icon,
  accent,
  to,
  a11yLabel,
}: {
  loading: boolean;
  value: string | null;
  label: string;
  icon: React.ReactNode;
  accent: string;
  to?: string;
  a11yLabel?: string;
}) {
  const body = (
    <>
      <div className="shrink-0" style={{ color: accent }}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        {loading ? (
          <Loader2 size={16} className="animate-spin text-primary" />
        ) : (
          <p className="text-lg font-semibold tabular-nums leading-none text-ink sm:text-xl">{value}</p>
        )}
        <p className="mt-0.5 text-[11px] leading-snug text-muted sm:text-xs">{label}</p>
      </div>
    </>
  );

  const className = 'flex flex-1 items-center gap-2.5 rounded-xl border border-border bg-surface px-3 py-2 sm:px-3.5 sm:py-2.5';

  if (to) {
    return (
      <Link
        to={to}
        aria-label={a11yLabel || label}
        className={`${className} transition hover:bg-canvas`}
      >
        {body}
      </Link>
    );
  }

  return <div className={className}>{body}</div>;
}

function TtqEntryCard({
  loading,
  burden,
  accent,
}: {
  loading: boolean;
  burden: HomeTtqBurdenStats | null;
  accent: string;
}) {
  const needReviewCount = loading
    ? null
    : (() => {
      const count = computeNeedReviewParticleCount(
        burden?.subjectParticleTotal,
        burden?.burdenReductionPercent,
      );
      return count == null ? '—' : formatStatValue(count);
    })();
  const burdenValue = loading ? null : formatBurdenValue(burden?.burdenReductionPercent);

  return (
    <Link
      to="/ttq"
      aria-label="进入品题"
      className="flex min-h-0 flex-1 items-stretch gap-2 rounded-xl border bg-surface p-3 transition hover:bg-canvas sm:p-3.5"
      style={{ borderColor: `${accent}33` }}
    >
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center self-start rounded-lg"
        style={{ backgroundColor: `${accent}14`, color: accent }}
      >
        <Utensils size={16} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] leading-snug text-muted sm:text-xs">只需复习颗粒数</p>
          {loading ? (
            <Loader2 size={16} className="mt-0.5 animate-spin" style={{ color: accent }} />
          ) : (
            <p className="mt-0.5 text-xl font-semibold tabular-nums leading-none sm:text-2xl" style={{ color: accent }}>
              {needReviewCount}
            </p>
          )}
        </div>
        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] leading-snug text-muted sm:text-xs">复习负担减少</p>
            {loading ? (
              <Loader2 size={14} className="mt-0.5 animate-spin" style={{ color: accent }} />
            ) : (
              <p className="mt-0.5 text-base font-semibold tabular-nums leading-none sm:text-lg" style={{ color: accent }}>
                {burdenValue}
              </p>
            )}
          </div>
          <MoreVertical size={16} className="shrink-0 text-subtle" aria-hidden />
        </div>
      </div>
    </Link>
  );
}

export function HomeCollectionStatsBanner({
  userId,
  refreshKey = '',
}: HomeCollectionStatsBannerProps) {
  const { activeSubject } = useActiveSubject();
  const subjectLabel = SUBJECT_CONFIG[activeSubject].label;
  const accent = SUBJECT_CONFIG[activeSubject].color;
  const showTtqEntry = isTtqSubjectEnabled(activeSubject);

  const [stats, setStats] = useState<HomeCollectionStats | null>(() => peekHomeCollectionStats(userId));
  const [statsLoading, setStatsLoading] = useState(() => !peekHomeCollectionStats(userId));
  const [ttqBurden, setTtqBurden] = useState<HomeTtqBurdenStats | null>(() => (
    showTtqEntry ? peekHomeTtqBurdenStats(userId, activeSubject) : null
  ));
  const [ttqLoading, setTtqLoading] = useState(() => (
    showTtqEntry ? !peekHomeTtqBurdenStats(userId, activeSubject) : false
  ));
  const [ttqCacheGeneration, setTtqCacheGeneration] = useState(getHomeTtqBurdenGeneration);

  useEffect(() => subscribeHomeTtqBurdenCache(() => {
    setTtqCacheGeneration(getHomeTtqBurdenGeneration());
  }), []);

  useEffect(() => {
    let cancelled = false;
    const cached = peekHomeCollectionStats(userId);
    if (cached) {
      setStats(cached);
      setStatsLoading(false);
    } else {
      setStatsLoading(true);
    }
    void loadHomeCollectionStats(userId)
      .then((next) => {
        if (!cancelled) setStats(next);
      })
      .catch(() => {
        if (!cancelled && !cached) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, refreshKey, activeSubject]);

  useEffect(() => {
    if (!showTtqEntry) {
      setTtqBurden(null);
      setTtqLoading(false);
      return;
    }

    let cancelled = false;
    const cached = peekHomeTtqBurdenStats(userId, activeSubject);
    if (cached) {
      setTtqBurden(cached);
      setTtqLoading(false);
      // 品题未变更：直接用缓存，不打网
      return;
    }

    setTtqLoading(true);
    void loadHomeTtqBurdenStats(userId, activeSubject)
      .then((next) => {
        if (!cancelled) setTtqBurden(next);
      })
      .catch(() => {
        if (!cancelled) setTtqBurden(null);
      })
      .finally(() => {
        if (!cancelled) setTtqLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, activeSubject, showTtqEntry, ttqCacheGeneration]);

  const particleTotal = statsLoading
    ? null
    : formatStatValue(resolveActiveSubjectParticleCount(stats, activeSubject));
  const reviewValue = statsLoading ? null : formatStatValue(resolveActiveSubjectReviewCount(stats, activeSubject));
  const coverageValue = statsLoading ? null : formatCoverageValue(resolveActiveSubjectCoveragePercent(stats, activeSubject));

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
        <div className="flex min-w-0 flex-[1.55] gap-2">
          <Link
            to="/knowledge"
            aria-label={`查看${subjectLabel}知识颗粒列表`}
            className="flex min-h-0 flex-1 flex-col justify-center gap-1 rounded-xl border bg-surface p-3 transition hover:bg-canvas sm:p-3.5"
            style={{ borderColor: `${accent}33` }}
          >
            <div className="flex items-center gap-2">
              <span
                className="flex h-8 w-8 items-center justify-center rounded-lg text-base"
                style={{ backgroundColor: `${accent}14`, color: accent }}
                aria-hidden
              >
                ⬢
              </span>
              <p className="text-[11px] leading-snug text-muted sm:text-xs">
                {subjectLabel} 颗粒数：
              </p>
            </div>
            {statsLoading ? (
              <Loader2 size={18} className="mt-0.5 animate-spin text-primary" />
            ) : (
              <p className="text-3xl font-semibold tabular-nums leading-none sm:text-4xl" style={{ color: accent }}>
                {particleTotal}
              </p>
            )}
          </Link>

          {showTtqEntry ? (
            <TtqEntryCard loading={ttqLoading} burden={ttqBurden} accent={accent} />
          ) : null}
        </div>

        <div className="flex min-w-0 flex-1 gap-2 sm:max-w-[14rem] sm:flex-col">
          <SideStatPanel
            loading={statsLoading}
            value={reviewValue}
            label="复习次数"
            accent={accent}
            icon={<Layers3 size={18} />}
          />
          <SideStatPanel
            loading={statsLoading}
            value={coverageValue}
            label="学习覆盖度"
            accent={accent}
            icon={<ChartPie size={18} />}
            to="/learning/spine-coverage"
            a11yLabel="查看学科主干覆盖"
          />
        </div>
      </div>
    </div>
  );
}
